import type { GeneratedAd } from "../components/adsUtils";

export function getResultId(ad: GeneratedAd): string | undefined {
  const result = ad.result as { id?: unknown } | null | undefined;
  return typeof result?.id === "string" ? result.id : undefined;
}

function getResultUrl(ad: GeneratedAd): string | undefined {
  if (ad.type === "image") return ad.result.output;
  return ad.result.video_output;
}

function recordValue(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : undefined;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function getUgcReferenceLineageKey(ad: GeneratedAd): string | undefined {
  if (ad.type !== "image") return undefined;
  const meta = recordValue(ad.metadata);
  if (meta?.assetKind !== "ugc-reference") return undefined;

  const videoIndex =
    typeof meta.ugcVideoIndex === "number" ? `v${meta.ugcVideoIndex}` : "v?";
  const ugcElement = recordValue(meta.ugcElement);
  const elementKey = stringValue(ugcElement?.elementKey);
  if (elementKey) {
    const planRunId = stringValue(ugcElement?.planRunId) ?? stringValue(meta.planRunId) ?? "run?";
    return `ugc-element:${planRunId}:${videoIndex}:${elementKey}`;
  }

  const ugcTextFrame = recordValue(meta.ugcTextFrame);
  const assignedBeat =
    stringValue(ugcTextFrame?.assignedBeat) ?? stringValue(meta.assignedBeat);
  if (assignedBeat) {
    const planRunId =
      stringValue(ugcTextFrame?.planRunId) ?? stringValue(meta.planRunId) ?? "run?";
    return `ugc-text-frame:${planRunId}:${videoIndex}:${assignedBeat}`;
  }

  return undefined;
}

function getUrlDedupKey(ad: GeneratedAd): string | undefined {
  const url = getResultUrl(ad);
  if (!url) return undefined;
  const lineageKey = getUgcReferenceLineageKey(ad);
  return lineageKey ? `${url}#${lineageKey}` : url;
}

// Dedup by both result.id AND output URL. Different completion paths stamp
// different ids for the same URL — RunningAgentMiniCard polling produces
// `${jobId}_${i}`, while SubAgentChatView's stream fallback uses
// `banan-direct-${hash(url)}` — so id-only dedup would let the same image
// land twice (once via each path) and surface as a duplicate in the asset
// tab + session strip.
//
// UGC reference artifacts are the exception: the adclone moodboard can render
// one continuity image and alias that URL to several element keys. Those rows
// are distinct logical assets even though the bitmap URL is shared, so their
// nested lineage is part of the URL dedupe key.
export function mergeGeneratedAds(existing: GeneratedAd[], incoming: GeneratedAd[]): GeneratedAd[] {
  const existingIds = new Set(existing.map(getResultId).filter(Boolean));
  const existingUrls = new Set(existing.map(getUrlDedupKey).filter(Boolean) as string[]);
  const newAds: GeneratedAd[] = [];
  for (const ad of incoming) {
    const id = getResultId(ad);
    if (!id || existingIds.has(id)) continue;
    const url = getUrlDedupKey(ad);
    if (url && existingUrls.has(url)) continue;
    existingIds.add(id);
    if (url) existingUrls.add(url);
    newAds.push(ad);
  }
  if (newAds.length === 0) return existing;
  return [...newAds, ...existing];
}

// Merge the variants produced by a finished Banan job, making the jobId-tagged
// entries (`${jobId}_${i}`) authoritative over any `banan-direct-${hash}` copies
// the live chat observer stream may have added for the same URLs first. The
// generic `mergeGeneratedAds` dedups by URL and keeps whichever landed first;
// when the observer wins that race the poller's jobId variants get dropped, so
// the ImageAdSetupModal (which matches images by jobId) can never resolve the
// run even though the images are visible everywhere else. Here we instead drop
// the colliding direct copies and dedup the incoming batch by id ONLY — a batch
// that legitimately repeats a URL keeps its distinct per-variant slots so the
// progress strip's variant count doesn't hang at "N-1 of N".
export function mergeCompletedImageAds(
  existing: GeneratedAd[],
  newAds: GeneratedAd[],
): GeneratedAd[] {
  if (newAds.length === 0) return existing;
  const newUrls = new Set(
    newAds
      .map(getResultUrl)
      .filter((url): url is string => Boolean(url)),
  );
  const survivingExisting = existing.filter((ad) => {
    if (ad.type !== "image") return true;
    const id = getResultId(ad) ?? "";
    const url = getResultUrl(ad);
    return !(id.startsWith("banan-direct") && url !== undefined && newUrls.has(url));
  });
  const existingIds = new Set(survivingExisting.map(getResultId).filter(Boolean));
  const seenIds = new Set<string>();
  const dedupedNew: GeneratedAd[] = [];
  for (const ad of newAds) {
    const id = getResultId(ad);
    if (!id || existingIds.has(id) || seenIds.has(id)) continue;
    seenIds.add(id);
    dedupedNew.push(ad);
  }
  if (dedupedNew.length === 0) return survivingExisting;
  return [...dedupedNew, ...survivingExisting];
}
